import React from 'react'

import { Invoice, PatchRequest, WebEdiDto } from '../../../../data/models'
import { requestPatchInvoiceById, useInvoiceById } from '../../data'
import { SummaryDto } from '../../data/model'
import { Summary } from './Summary'

type Props = {
  invoiceId: string
}

export const DataHandlingSummary: React.FC<Props> = ({ invoiceId }) => {
  const { data: invoice, isLoading } = useInvoiceById(invoiceId)

  const dataToFormValues = (data: WebEdiDto<Invoice>): SummaryDto => {
    return {
      confirmationTotalNetItemAmount:
        data?.Payload.ConfirmationTotalNetItemAmount || 0,
      confirmationTotalNetItemAmountChecked:
        data?.Payload.Meta?.Fields?.DisabledCalculation
          ?.ConfirmationTotalNetItemAmount || false,
      confirmationSurcharge: data?.Payload.ConfirmationSurcharge || 0,
      confirmationNetAmount: data?.Payload.ConfirmationNetAmount || 0,
      confirmationNetAmountChecked:
        data?.Payload.Meta?.Fields?.DisabledCalculation
          ?.ConfirmationNetAmount || false,
      confirmationTaxRate: data?.Payload.ConfirmationTaxRate || 0,
      confirmationTaxValue: data?.Payload.ConfirmationTaxValue || 0,
      confirmationTaxValueChecked:
        data?.Payload.Meta?.Fields?.DisabledCalculation?.ConfirmationTaxValue ||
        false,
      confirmationInvoiceAmount: data?.Payload.ConfirmationInvoiceAmount || 0,
      confirmationInvoiceAmountChecked:
        data?.Payload.Meta?.Fields?.DisabledCalculation
          ?.ConfirmationInvoiceAmount || false,
      currency: data?.Payload.OrderCurrency,
    }
  }

  const patchFromFormValues = (
    formData: SummaryDto,
  ): PatchRequest<WebEdiDto<Invoice>> => {
    const patchOperations: PatchRequest<WebEdiDto<Invoice>> = []

    // confirmationTotalNetItemAmount
    if (
      formData.confirmationTotalNetItemAmount !==
      invoice?.Payload.ConfirmationTotalNetItemAmount
    ) {
      patchOperations.push({
        op: invoice?.Payload.ConfirmationTotalNetItemAmount ? 'replace' : 'add',
        path: '/Payload/ConfirmationTotalNetItemAmount',
        value: formData.confirmationTotalNetItemAmount,
      })
    }

    if (
      formData.confirmationTotalNetItemAmountChecked !==
      invoice?.Payload.Meta?.Fields?.DisabledCalculation
        ?.ConfirmationTotalNetItemAmount
    ) {
      patchOperations.push({
        op: invoice?.Payload.Meta?.Fields?.DisabledCalculation
          ?.ConfirmationTotalNetItemAmount
          ? 'replace'
          : 'add',
        path: '/Payload/Meta/Fields/DisabledCalculation/ConfirmationTotalNetItemAmount',
        value: formData.confirmationTotalNetItemAmountChecked,
      })
    }

    // confirmationSurcharge
    if (
      formData.confirmationSurcharge !== invoice?.Payload.ConfirmationSurcharge
    ) {
      patchOperations.push({
        op: invoice?.Payload.ConfirmationSurcharge ? 'replace' : 'add',
        path: '/Payload/ConfirmationSurcharge',
        value: formData.confirmationSurcharge,
      })
    }

    // confirmationNetAmount
    if (
      formData.confirmationNetAmount !== invoice?.Payload.ConfirmationNetAmount
    ) {
      patchOperations.push({
        op: invoice?.Payload.ConfirmationNetAmount ? 'replace' : 'add',
        path: '/Payload/ConfirmationNetAmount',
        value: formData.confirmationNetAmount,
      })
    }

    if (
      formData.confirmationNetAmountChecked !==
      invoice?.Payload.Meta?.Fields?.DisabledCalculation?.ConfirmationNetAmount
    ) {
      patchOperations.push({
        op: invoice?.Payload.Meta?.Fields?.DisabledCalculation
          ?.ConfirmationNetAmount
          ? 'replace'
          : 'add',
        path: '/Payload/Meta/Fields/DisabledCalculation/ConfirmationNetAmount',
        value: formData.confirmationNetAmountChecked,
      })
    }

    // confirmationTaxRate
    if (formData.confirmationTaxRate !== invoice?.Payload.ConfirmationTaxRate) {
      patchOperations.push({
        op: invoice?.Payload.ConfirmationTaxRate ? 'replace' : 'add',
        path: '/Payload/ConfirmationTaxRate',
        value: formData.confirmationTaxRate,
      })
    }

    // confirmationTaxValue
    if (
      formData.confirmationTaxValue !== invoice?.Payload.ConfirmationTaxValue
    ) {
      patchOperations.push({
        op: invoice?.Payload.ConfirmationTaxValue ? 'replace' : 'add',
        path: '/Payload/ConfirmationTaxValue',
        value: formData.confirmationTaxValue,
      })
    }

    if (
      formData.confirmationTaxValueChecked !==
      invoice?.Payload.Meta?.Fields?.DisabledCalculation?.ConfirmationTaxValue
    ) {
      patchOperations.push({
        op: invoice?.Payload.Meta?.Fields?.DisabledCalculation
          ?.ConfirmationTaxValue
          ? 'replace'
          : 'add',
        path: '/Payload/Meta/Fields/DisabledCalculation/ConfirmationTaxValue',
        value: formData.confirmationTaxValueChecked,
      })
    }

    // confirmationInvoiceAmount
    if (
      formData.confirmationInvoiceAmount !==
      invoice?.Payload.ConfirmationInvoiceAmount
    ) {
      patchOperations.push({
        op: invoice?.Payload.ConfirmationInvoiceAmount ? 'replace' : 'add',
        path: '/Payload/ConfirmationInvoiceAmount',
        value: formData.confirmationInvoiceAmount,
      })
    }

    if (
      formData.confirmationInvoiceAmountChecked !==
      invoice?.Payload.Meta?.Fields?.DisabledCalculation
        ?.ConfirmationInvoiceAmount
    ) {
      patchOperations.push({
        op: invoice?.Payload.Meta?.Fields?.DisabledCalculation
          ?.ConfirmationInvoiceAmount
          ? 'replace'
          : 'add',
        path: '/Payload/Meta/Fields/DisabledCalculation/ConfirmationInvoiceAmount',
        value: formData.confirmationInvoiceAmountChecked,
      })
    }

    return patchOperations
  }

  const onFormSubmit = async (formData: SummaryDto) => {
    const patchData: PatchRequest<WebEdiDto<Invoice>> =
      patchFromFormValues(formData)
    await requestPatchInvoiceById(invoiceId, patchData)
    // mutate()
  }

  return (
    <Summary
      data={dataToFormValues(invoice!)}
      onSubmit={onFormSubmit}
      isLoading={isLoading}
    />
  )
}
